import {
  apiRealtimeUrl,
  connectApiRealtime,
  type ApiRealtimeClient,
  type ApiRealtimeNotificationEvent,
} from '@doneverse/data/api'
import { contactsOptions, messagesInfiniteOptions, queryKeys } from '@doneverse/data/queries'
import type { ContactMessage } from '@doneverse/shared'
import { type InfiniteData, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { playNotificationSound, prepareNotificationSound } from '@/lib/notification-sound'
import { useDashboardStore } from '@/store'

interface UseNotificationSubscriptionOptions {
  userId: string | undefined
  /**
   * Returns a fresh Auth0 access token (audience: the API). Called once on
   * every connect/reconnect — token rotation is handled by the auth layer.
   */
  getToken: () => Promise<string>
  enabled?: boolean
  onNewNotification?: (event: ApiRealtimeNotificationEvent['data']) => void
}

type MessagesInfiniteData = InfiniteData<ContactMessage[]>

/**
 * Append a live message to the cached thread, newest at the end of page 0.
 * No-op when the thread isn't cached (opening it will fetch fresh) or when the
 * message is already present (dedupe against an earlier append / refetch).
 */
function appendIncomingMessage(
  old: MessagesInfiniteData | undefined,
  message: ContactMessage,
): MessagesInfiniteData | undefined {
  if (!old) return old
  if (old.pages.some((page) => page.some((m) => m.id === message.id))) return old
  return {
    ...old,
    pages: old.pages.map((page, index) => (index === 0 ? [...page, message] : page)),
  }
}

/**
 * Subscribe to the API WebSocket at /v1/realtime.
 *
 * - `message.created` → append the message to the open chat directly (no
 *   refetch) and refresh the contacts list (unread + last-message preview).
 * - `notification.created` → invalidate queries + toast (suppressed for the
 *   chat already on screen, as a backstop to the server's focus suppression).
 * - Reports the focused chat to the server (`set_focus`) so it can skip
 *   notifications for a conversation the user is actively reading.
 *
 * The API owns the realtime contract, see
 * docs/api-websockets-supabase-realtime.md.
 */
export function useNotificationSubscription({
  userId,
  getToken,
  enabled = true,
  onNewNotification,
}: UseNotificationSubscriptionOptions): void {
  const queryClient = useQueryClient()
  const clientRef = useRef<ApiRealtimeClient | null>(null)
  // Track the open chat reactively so we can push focus changes to the socket
  // without re-subscribing it.
  const openChatContactId = useDashboardStore((s) => s.openChatContactId)

  // Web Audio must be unlocked from a user gesture in browsers with autoplay
  // protection before a later WebSocket event can make a sound.
  useEffect(() => {
    if (!enabled || !userId) return
    return prepareNotificationSound()
  }, [enabled, userId])

  useEffect(() => {
    if (!enabled || !userId) return

    const apiBaseUrl = (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:3001'
    const url = apiRealtimeUrl(apiBaseUrl)

    const client = connectApiRealtime({
      url,
      getToken,
      onMessage: (event) => {
        const threadKey = messagesInfiniteOptions(event.contact_id).queryKey
        // Append for instant feedback...
        queryClient.setQueryData<MessagesInfiniteData>(threadKey, (old) =>
          appendIncomingMessage(old, event.data),
        )
        // ...then invalidate that one thread so the append can't be lost: if a
        // fetch was in flight (started before the message was inserted) it would
        // otherwise clobber the append, and a focused chat gets no
        // notification.created fallback to recover it. Targeted — one thread,
        // not the nuclear invalidate the notification path uses.
        void queryClient.invalidateQueries({ queryKey: threadKey })
        // Refresh unread counts + last-message previews too.
        void queryClient.invalidateQueries({ queryKey: contactsOptions().queryKey })
      },
      onConnected: () => {
        // Call events are at-most-once with no replay, so a huddle that started
        // while this socket was down would otherwise be invisible until the
        // user navigated. Re-read on every (re)connect.
        void queryClient.invalidateQueries({ queryKey: queryKeys.calls })
      },
      onCallEvent: (event) => {
        // Read the store imperatively (never via a hook dep) so huddle state
        // changes can't re-subscribe the socket. The event → store reducer
        // lives on the call slice.
        useDashboardStore.getState().applyCallEvent(event)

        // Roster churn changes nothing the query cache holds — the store
        // update above is the whole effect. Only a start/end alters the
        // thread card ("started a huddle" ↔ "Huddle ended · N min") or the
        // active-call read.
        if (event.type === 'call.participants_changed') return

        void queryClient.invalidateQueries({
          queryKey: messagesInfiniteOptions(event.contact_id).queryKey,
        })
        void queryClient.invalidateQueries({ queryKey: queryKeys.calls })
      },
      onNotification: (event) => {
        void queryClient.invalidateQueries()

        // Stay quiet about a new message for the chat already on screen — the
        // user is reading it live, so a toast would just be noise. Backstops
        // the server's focus suppression for the race window before focus
        // reaches the cache. Read the open chat imperatively so this never
        // re-subscribes the socket.
        const currentOpenChat = useDashboardStore.getState().openChatContactId
        const isMessageForOpenChat =
          event.data.type === 'new_message' &&
          currentOpenChat !== null &&
          event.data.reference_id === currentOpenChat

        // Incoming calls own their foreground announcement: the global popup +
        // ringtone (IncomingCallHost, fed by the `call.started` event above).
        // A generic toast + chime on top would double-announce every call.
        // The OS push for background tabs is unaffected (see sw.js).
        const isCallStarted = event.data.type === 'call_started'

        if (!isMessageForOpenChat && !isCallStarted) {
          toast(event.data.title, {
            description: event.data.body ?? undefined,
          })

          // The service worker intentionally suppresses the OS notification
          // while a dashboard window is focused. Pair the in-app toast with a
          // chime in exactly that case, without doubling the OS notification's
          // sound when this tab is in the background. Honor the user's sound
          // preference (read imperatively so it can't re-subscribe the socket).
          if (document.visibilityState === 'visible' && document.hasFocus()) {
            const { notificationSoundEnabled, notificationSoundId } = useDashboardStore.getState()
            if (notificationSoundEnabled) playNotificationSound(notificationSoundId)
          }
        }

        onNewNotification?.(event.data)
      },
    })
    clientRef.current = client
    // Seed focus from the current store value so a reconnect (new client)
    // immediately re-reports it.
    client.setFocus(useDashboardStore.getState().openChatContactId)

    return () => {
      clientRef.current = null
      client.close()
    }
  }, [userId, enabled, getToken, queryClient, onNewNotification])

  // Push focus changes to the live socket without tearing it down.
  useEffect(() => {
    clientRef.current?.setFocus(openChatContactId)
  }, [openChatContactId])
}
